import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import API_CONFIG from '../config/api';
import { useTranslation } from '../i18n/useTranslation';
import { useSession } from '../contexts/SessionContext';
import { useSessionExpiration } from '../hooks/useSessionExpiration';
import { usePostActionBar } from '../contexts/PostActionBarContext';
import SelectorCustom from './selector-custom';

import { ReactComponent as LogoTypoBold} from '../asset/logo/typo-bold.svg';
import { ReactComponent as Logo} from '../asset/logo/logo.svg';
import { ReactComponent as HomeIcon } from '../asset/gnb/home.svg';
import { ReactComponent as CommunityIcon } from '../asset/gnb/community.svg';
import { ReactComponent as GigStarIcon } from '../asset/gnb/GIGstar.svg';
import { ReactComponent as ProfileIcon } from '../asset/gnb/profile.svg';

function GNB1({ isMobile: isMobileProp, isMobileMenuOpen = false, onToggleMobileMenu }) {
    const location = useLocation();
    const pathname = location.pathname;
    const navigate = useNavigate();
    const { t, language, setLanguage, languages } = useTranslation();
    const { expiresIn, verifyAuth, setExpiresIn, isVerifying } = useSession();
    const { remainingSeconds, handleExtendSession } = useSessionExpiration({
      verifyAuth,
      expiresIn,
      setExpiresIn,
      isVerifying
    });

    const { isActive: isActionBarActive } = usePostActionBar();
    const [loggedInUserId, setLoggedInUserId] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [isMobileLocal, setIsMobileLocal] = useState(window.innerWidth <= 768);
    const [isExtendingSession, setIsExtendingSession] = useState(false);
    const isMobile = isMobileProp !== undefined ? isMobileProp : isMobileLocal;

    // 검색 관련 상태
    const [searchScope, setSearchScope] = useState('all'); // 'all', 'posts', 'categories', 'users', 'gigs'
    const [searchQuery, setSearchQuery] = useState('');
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const [autocompleteResults, setAutocompleteResults] = useState({
        posts: [],
        categories: [],
        users: [],
        gigs: []
    });
    const [searchLoading, setSearchLoading] = useState(false);
    const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
    const searchInputRef = useRef(null);
    const searchOverlayRef = useRef(null);
    const mobileSearchInputRef = useRef(null);
    const debounceTimerRef = useRef(null);

    const searchScopeOptions = useMemo(() => ([
        { value: 'all', label: t('search.scope.all') },
        { value: 'posts', label: t('search.scope.posts') },
        { value: 'categories', label: t('search.scope.categories') },
        { value: 'users', label: t('search.scope.users') },
        { value: 'gigs', label: t('search.scope.gigs') },
    ]), [t]);

    const languageOptions = useMemo(
        () => Object.entries(languages).map(([code, lang]) => ({ value: code, label: lang.name })),
        [languages]
    );

    const openSearchOverlay = useCallback(() => {
        setIsSearchOverlayOpen(true);
        requestAnimationFrame(() => {
            if (!searchInputRef.current || window.innerWidth <= 768) return;
            const rect = searchInputRef.current.getBoundingClientRect();
            document.documentElement.style.setProperty('--gnb-search-left', `${rect.left}px`);
            document.documentElement.style.setProperty('--gnb-search-width', `${rect.width}px`);
            document.documentElement.style.setProperty('--gnb-search-top', `${rect.bottom + 8}px`);
        });
    }, []);

    const closeSearchOverlay = useCallback(() => {
        setIsSearchOverlayOpen(false);
        setShowAutocomplete(false);
    }, []);

    const syncSearchOverlayPosition = useCallback(() => {
        if (!searchInputRef.current || isMobile) {
            document.documentElement.style.removeProperty('--gnb-search-left');
            document.documentElement.style.removeProperty('--gnb-search-width');
            document.documentElement.style.removeProperty('--gnb-search-top');
            return;
        }

        const rect = searchInputRef.current.getBoundingClientRect();
        document.documentElement.style.setProperty('--gnb-search-left', `${rect.left}px`);
        document.documentElement.style.setProperty('--gnb-search-width', `${rect.width}px`);
        document.documentElement.style.setProperty('--gnb-search-top', `${rect.bottom + 8}px`);
    }, [isMobile]);

    useEffect(() => {
      const syncLoggedInUserId = () => {
        const data = authService.getUserData();
        setLoggedInUserId(data.userId || data.id || null);
        setProfileImage(data.profile_image || null);
      };

      syncLoggedInUserId();
      window.addEventListener('userDataChanged', syncLoggedInUserId);

      const handleStorageChange = (e) => {
        if (e.key === 'id' || e.key === 'auth_token' || e.key === 'profile_image') {
          syncLoggedInUserId();
        }
      };
      window.addEventListener('storage', handleStorageChange);

      return () => {
        window.removeEventListener('userDataChanged', syncLoggedInUserId);
        window.removeEventListener('storage', handleStorageChange);
      };
    }, []);

    // 화면 크기 감지
    useEffect(() => {
        const handleResize = () => {
            setIsMobileLocal(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 남은 시간 포맷팅 함수
    const formatTime = (seconds) => {
      if (seconds === null || seconds === undefined || seconds <= 0) {
        return null;
      }
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // ✅ remainingSeconds를 우선 사용 (실시간 카운트다운), 없으면 expiresIn 사용
    const displayTime = formatTime(remainingSeconds !== null ? remainingSeconds : expiresIn);

    const handleSessionTimeClick = useCallback(async () => {
        if (isExtendingSession || isVerifying || !authService.isLoggedIn()) return;
        setIsExtendingSession(true);
        try {
            await handleExtendSession();
        } catch (error) {
            console.error('[GNB1] 세션 연장 실패:', error);
        } finally {
            setIsExtendingSession(false);
        }
    }, [handleExtendSession, isExtendingSession, isVerifying]);

    useEffect(() => {
        if (isSearchOverlayOpen) {
            document.body.classList.add('search-overlay-active');
        } else {
            document.body.classList.remove('search-overlay-active');
        }
        return () => document.body.classList.remove('search-overlay-active');
    }, [isSearchOverlayOpen]);

    useEffect(() => {
        closeSearchOverlay();
    }, [pathname, closeSearchOverlay]);

    useEffect(() => {
        if (!isSearchOverlayOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeSearchOverlay();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isSearchOverlayOpen, closeSearchOverlay]);

    useEffect(() => {
        if (isSearchOverlayOpen && isMobile) {
            requestAnimationFrame(() => mobileSearchInputRef.current?.focus());
        }
    }, [isSearchOverlayOpen, isMobile]);

    useEffect(() => {
        if (!isSearchOverlayOpen) {
            document.documentElement.style.removeProperty('--gnb-search-left');
            document.documentElement.style.removeProperty('--gnb-search-width');
            document.documentElement.style.removeProperty('--gnb-search-top');
            return undefined;
        }

        syncSearchOverlayPosition();
        window.addEventListener('resize', syncSearchOverlayPosition);
        window.addEventListener('scroll', syncSearchOverlayPosition, true);

        return () => {
            window.removeEventListener('resize', syncSearchOverlayPosition);
            window.removeEventListener('scroll', syncSearchOverlayPosition, true);
            document.documentElement.style.removeProperty('--gnb-search-left');
            document.documentElement.style.removeProperty('--gnb-search-width');
            document.documentElement.style.removeProperty('--gnb-search-top');
        };
    }, [isSearchOverlayOpen, syncSearchOverlayPosition]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isInsideDesktopSearch = searchInputRef.current?.contains(event.target);
            const isInsideOverlay = searchOverlayRef.current?.contains(event.target);

            if (isSearchOverlayOpen && !isInsideDesktopSearch && !isInsideOverlay) {
                closeSearchOverlay();
            } else if (!isSearchOverlayOpen && !isInsideDesktopSearch) {
                setShowAutocomplete(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [isSearchOverlayOpen, closeSearchOverlay]);

    const renderProfileButtonContent = () => {
        if (authService.isLoggedIn() && loggedInUserId) {
            return (
                <img
                    src={profileImage ? API_CONFIG.resolveFileUrl(profileImage) : API_CONFIG.getDefaultAvatar(loggedInUserId)}
                    className="img-profile-round"
                    alt="Profile"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = API_CONFIG.getDefaultAvatar(loggedInUserId);
                    }}
                />
            );
        }
        return <ProfileIcon className="gnb-menu-svg-icon" aria-hidden="true" />;
    };

    const handleProfileClick = () => {
        const data = authService.getUserData();
        const userId = loggedInUserId || data.userId || data.id;
        if (authService.isLoggedIn() && userId) {
            navigate(`/profile?userId=${userId}&item=home`);
        } else if (window.openLoginPopup) {
            window.openLoginPopup();
        }
    };

    // 검색 자동완성
    const searchAutocomplete = async (query) => {
        if (!query.trim()) {
            setAutocompleteResults({ posts: [], categories: [], users: [], gigs: [] });
            setShowAutocomplete(false);
            return;
        }

        setSearchLoading(true);
        try {
            const results = { posts: [], categories: [], users: [], gigs: [] };

            // 검색 범위에 따라 API 호출
            if (searchScope === 'all' || searchScope === 'posts') {
                // 게시글 검색
                try {
                    const postsRes = await fetch(`/api/posts?title=${encodeURIComponent(query)}&pageSize=5`);
                    const postsData = await postsRes.json();
                    const posts = postsData.data?.posts || postsData.posts || [];
                    results.posts = posts.slice(0, 5);
                } catch (err) {
                    console.error('게시글 검색 실패:', err);
                }
            }

            if (searchScope === 'all' || searchScope === 'categories') {
                // 카테고리 검색 (응답: 배열 또는 { success, categories })
                try {
                    const categoriesRes = await fetch(`/api/categories?search=${encodeURIComponent(query)}`);
                    const categoriesData = await categoriesRes.json();
                    const categories = Array.isArray(categoriesData) ? categoriesData : (categoriesData.categories || []);
                    results.categories = categories.slice(0, 5);
                } catch (err) {
                    console.error('카테고리 검색 실패:', err);
                }
            }

            if (searchScope === 'all' || searchScope === 'users') {
                // 사용자 검색
                try {
                    const usersRes = await fetch(`/api/search-users?nickname=${encodeURIComponent(query)}`);
                    const usersData = await usersRes.json();
                    if (usersData.success && usersData.users) {
                        results.users = usersData.users.slice(0, 5);
                    }
                } catch (err) {
                    console.error('사용자 검색 실패:', err);
                }
            }

            if (searchScope === 'all' || searchScope === 'gigs') {
                // GIG 프로젝트 검색 (목업)
                try {
                    // TODO: 실제 API 구현 시 교체
                    // const gigsRes = await fetch(`/api/gigs/search?q=${encodeURIComponent(query)}&pageSize=5`);
                    // const gigsData = await gigsRes.json();
                    // if (gigsData.success && gigsData.projects) {
                    //     results.gigs = gigsData.projects.slice(0, 5);
                    // }
                    // 목업 데이터
                    results.gigs = [];
                } catch (err) {
                    console.error('GIG 프로젝트 검색 실패:', err);
                }
            }

            setAutocompleteResults(results);
            setShowAutocomplete(
                results.posts.length > 0 || 
                results.categories.length > 0 || 
                results.users.length > 0 ||
                results.gigs.length > 0
            );
        } catch (err) {
            console.error('자동완성 검색 실패:', err);
        } finally {
            setSearchLoading(false);
        }
    };

    // 검색어 입력 핸들러 (디바운스)
    const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // 디바운스: 200ms 후 검색 실행
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (value.trim()) {
            debounceTimerRef.current = setTimeout(() => {
                searchAutocomplete(value);
            }, 200);
        } else {
            setAutocompleteResults({ posts: [], categories: [], users: [], gigs: [] });
            setShowAutocomplete(false);
        }
    };

    // 자동완성 항목 클릭
    const handleAutocompleteClick = (type, item) => {
        closeSearchOverlay();
        
        switch (type) {
            case 'post':
                navigate(`/post/${item.id}`);
                break;
            case 'category':
                navigate(`/community?category=${encodeURIComponent(item.name || item.id)}`);
                break;
            case 'user':
                navigate(`/profile?userId=${item.id}&item=home`);
                break;
            case 'gig':
                navigate(`/gig/space/${item.id}`);
                break;
            default:
                break;
        }
    };

    // 검색 실행
    const handleSearch = () => {
        if (!searchQuery.trim()) return;
        closeSearchOverlay();
        if (searchScope === 'gigs') {
            navigate(`/gig/explore?search=${encodeURIComponent(searchQuery)}`);
        } else {
            navigate(`/search-result?q=${encodeURIComponent(searchQuery)}&scope=${searchScope}`);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        } else if (e.key === 'Escape') {
            closeSearchOverlay();
            searchInputRef.current?.blur();
            mobileSearchInputRef.current?.blur();
        }
    };

    const hasAutocompleteResults =
        autocompleteResults.posts.length > 0 ||
        autocompleteResults.categories.length > 0 ||
        autocompleteResults.users.length > 0 ||
        autocompleteResults.gigs.length > 0;

    const renderAutocompleteContent = () => (
        searchLoading ? (
            <div className="gnb-search-loading">
                {t('common.searching') || '검색 중...'}
            </div>
        ) : (
            <>
                {autocompleteResults.posts.length > 0 && (
                    <div className="gnb-search-autocomplete-section">
                        <div className="gnb-search-autocomplete-header">
                            {t('search.autocomplete.posts')}
                        </div>
                        {autocompleteResults.posts.map((post) => (
                            <div
                                key={`post-${post.id}`}
                                className="gnb-search-autocomplete-item"
                                onClick={() => handleAutocompleteClick('post', post)}
                            >
                                <div className="gnb-search-autocomplete-item-title">
                                    {post.title}
                                </div>
                                <div className="gnb-search-autocomplete-item-meta">
                                    {post.author_nickname || post.author} · {post.category}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {autocompleteResults.categories.length > 0 && (
                    <div className="gnb-search-autocomplete-section">
                        <div className="gnb-search-autocomplete-header">
                            {t('search.communityCategories') || '커뮤니티 카테고리'}
                        </div>
                        {autocompleteResults.categories.map((category) => (
                            <div
                                key={`category-${category.id || category.name}`}
                                className="gnb-search-autocomplete-item"
                                onClick={() => handleAutocompleteClick('category', category)}
                            >
                                <div className="gnb-search-autocomplete-item-title">
                                    {category.name}
                                </div>
                                <div className="gnb-search-autocomplete-item-meta">
                                    {category.description || t('search.autocomplete.categories')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {autocompleteResults.users.length > 0 && (
                    <div className="gnb-search-autocomplete-section">
                        <div className="gnb-search-autocomplete-header">
                            {t('search.autocomplete.users')}
                        </div>
                        {autocompleteResults.users.map((user) => (
                            <div
                                key={`user-${user.id}`}
                                className="gnb-search-autocomplete-item"
                                onClick={() => handleAutocompleteClick('user', user)}
                            >
                                <div className="gnb-search-autocomplete-item-title">
                                    {user.nickname}
                                </div>
                                <div className="gnb-search-autocomplete-item-meta">
                                    {user.email || user.profile_intro}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {autocompleteResults.gigs.length > 0 && (
                    <div className="gnb-search-autocomplete-section">
                        <div className="gnb-search-autocomplete-header">
                            GIG 프로젝트
                        </div>
                        {autocompleteResults.gigs.map((gig) => (
                            <div
                                key={`gig-${gig.id}`}
                                className="gnb-search-autocomplete-item"
                                onClick={() => handleAutocompleteClick('gig', gig)}
                            >
                                <div className="gnb-search-autocomplete-item-title">
                                    {gig.title || gig.name}
                                </div>
                                <div className="gnb-search-autocomplete-item-meta">
                                    {gig.description || '프로젝트'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!hasAutocompleteResults && searchQuery.trim() && (
                    <div className="gnb-search-autocomplete-empty">
                        {t('search.noResults') || '검색 결과가 없습니다'}
                    </div>
                )}

                {!searchQuery.trim() && (
                    <div className="gnb-search-autocomplete-empty">
                        {t('search.discoveryPlaceholder')}
                    </div>
                )}
            </>
        )
    );

    const renderSearchScopeSelect = () => (
        <SelectorCustom
            value={searchScope}
            options={searchScopeOptions}
            onChange={(e) => setSearchScope(e.target.value)}
            triggerVariant="primary"
            wrapperClassName="gnb-search-scope-select"
            showChevron={false}
            stopPropagation
            style={{ minWidth: 70 }}
        />
    );

    return(
        <>
        <header>
            <nav>
                <Link to="/" className="logo-int" aria-label="홈으로 이동">
                    <Logo className="gnb-logo-symbol" aria-hidden="true" />
                    <LogoTypoBold className="gnb-logo-typo" aria-hidden="true" />
                </Link>
                <ul id="gnb">
                    <div
                        className="gnb-search-container"
                        ref={searchInputRef}
                        onClick={openSearchOverlay}
                    >
                        {renderSearchScopeSelect()}
                        <input 
                            type="text" 
                            placeholder={t('search.discoveryPlaceholder')}
                            className="gnb-search-input" 
                            value={searchQuery}
                            onChange={handleSearchInput}
                            onKeyDown={handleSearchKeyDown}
                            onFocus={() => {
                                openSearchOverlay();
                                if (searchQuery.trim() && hasAutocompleteResults) {
                                    setShowAutocomplete(true);
                                }
                            }}
                        />

                        {/* 세로 구분선 */}
                        <div className="gnb-search-divider"></div>

                        {/* 검색 버튼 */}
                        <button 
                            className="btn-sqr btn-trans" 
                            onClick={handleSearch}
                            type="button"
                            title={t('common.search') || '검색'}
                        >
                            <span className="material-symbols-rounded">search</span>
                        </button>

                        {/* 데스크톱: 오버레이 미사용 시에만 인라인 드롭다운 */}
                        {!isSearchOverlayOpen && showAutocomplete && (
                            <div className="gnb-search-autocomplete">
                                {renderAutocompleteContent()}
                            </div>
                        )}
                    </div>
                </ul>
                <div className="btn-group">
                    {isMobile && (
                        <button
                            type="button"
                            className="btn-sqr btn-trans gnb-search-mobile-btn"
                            onClick={openSearchOverlay}
                            title={t('common.search') || '검색'}
                            aria-label={t('common.search') || '검색'}
                        >
                            <span className="material-symbols-rounded">search</span>
                        </button>
                    )}
                    {!isMobile && displayTime !== null && authService.isLoggedIn() && (
                        <button
                            type="button"
                            className={`badge-rtg ${
                                (remainingSeconds !== null ? remainingSeconds : expiresIn) >= 1200 ? 'badge-success' : 
                                (remainingSeconds !== null ? remainingSeconds : expiresIn) >= 300 ? 'badge-warning' : 
                                'badge-failure'
                            }`}
                            style={{ 
                                minWidth: '60px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                opacity: isExtendingSession ? 0.7 : 1,
                                border: 'none'
                            }}
                            title="클릭하여 로그인 시간 연장"
                            onClick={handleSessionTimeClick}
                            disabled={isExtendingSession || isVerifying}
                            aria-label="로그인 시간 연장"
                        >
                            {isExtendingSession ? '...' : displayTime}
                        </button>
                    )}
                    <SelectorCustom
                        value={language}
                        options={languageOptions}
                        onChange={(e) => setLanguage(e.target.value)}
                        triggerVariant="icon"
                        triggerIcon="language"
                        triggerTitle={t('language.select')}
                        wrapperClassName="gnb-language-select"
                        menuAlign="right"
                        showChevron={false}
                        style={{ minWidth: 'auto' }}
                    />
                    {isMobile && onToggleMobileMenu ? (
                        <button
                            type="button"
                            className={`btn-sqr btn-trans${isMobileMenuOpen ? ' active' : ''}`}
                            id="gnb-menu-btn"
                            onClick={onToggleMobileMenu}
                            aria-label={isMobileMenuOpen ? t('common.close') : (t('nav.menu') || '메뉴')}
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="material-symbols-rounded">
                                {isMobileMenuOpen ? 'close' : 'menu'}
                            </span>
                        </button>
                    ) : (
                        <button className="btn-sqr btn-trans" id="gnb-login-link" onClick={handleProfileClick}>
                            {renderProfileButtonContent()}
                        </button>
                    )}
                </div>
            </nav>
        </header>
        <aside>
            <div id="gnb-mobile" className={isActionBarActive ? 'gnb-mobile--hidden' : undefined}>
                <Link to="/" className={`btn-gnb-mobile ${pathname === '/' ? 'active' : ''}`}>
                    <HomeIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                    <h6>{t('nav.home')}</h6>
                </Link>
                <Link to="/community" className={`btn-gnb-mobile ${pathname.startsWith('/community') ? 'active' : ''}`}>
                    <CommunityIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                    <h6>{t('nav.community')}</h6>
                </Link>
                <Link to="/gig" className={`btn-gnb-mobile ${pathname.startsWith('/gig') ? 'active' : ''}`}>
                    <GigStarIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                    <h6>{t('nav.gig')}</h6>
                </Link>
                <Link to="/synapse" className={`btn-gnb-mobile ${pathname.startsWith('/synapse') ? 'active' : ''}`}>
                    <span className="material-symbols-rounded">hub</span>
                    <h6>{t('nav.synapse')}</h6>
                </Link>
                <button
                    type="button"
                    className={`btn-gnb-mobile ${pathname.startsWith('/profile') && loggedInUserId ? 'active' : ''}`}
                    onClick={handleProfileClick}
                >
                    <ProfileIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                    <h6>{t('nav.profile')}</h6>
                </button>
            </div>
        </aside>

        {isSearchOverlayOpen && createPortal(
            <div className="gnb-search-overlay" ref={searchOverlayRef}>
                <button
                    type="button"
                    className="gnb-search-overlay-backdrop"
                    onClick={closeSearchOverlay}
                    aria-label={t('common.close') || '닫기'}
                />
                <div className="gnb-search-overlay-panel">
                    {isMobile && (
                        <div className="gnb-search-overlay-header">
                            <div className="gnb-search-container gnb-search-container--overlay">
                                {renderSearchScopeSelect()}
                                <input
                                    ref={mobileSearchInputRef}
                                    type="text"
                                    placeholder={t('search.discoveryPlaceholder')}
                                    className="gnb-search-input"
                                    value={searchQuery}
                                    onChange={handleSearchInput}
                                    onKeyDown={handleSearchKeyDown}
                                />

                                <div className="gnb-search-divider"></div>

                                <button
                                    className="btn-sqr btn-trans"
                                    onClick={handleSearch}
                                    type="button"
                                    title={t('common.search') || '검색'}
                                >
                                    <span className="material-symbols-rounded">search</span>
                                </button>
                            </div>

                            <button
                                type="button"
                                className="btn-sqr btn-trans gnb-search-overlay-close"
                                onClick={closeSearchOverlay}
                                aria-label={t('common.close') || '닫기'}
                            >
                                <span className="material-symbols-rounded">close</span>
                            </button>
                        </div>
                    )}

                    <div className="gnb-search-overlay-results">
                        {renderAutocompleteContent()}
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
}

export default GNB1;